/**
 * Seed insights for dev / mock mode. Varied type + status, several tied to the
 * mock customers in `mockCustomers.ts` (`cust-001` … `cust-005`).
 */

import type { Insight } from '@/types/insight.types'

const now = Date.now()
const daysAgo = (d: number): string => new Date(now - d * 24 * 60 * 60 * 1000).toISOString()

export const mockInsights: Insight[] = [
  {
    id: 'ins-001',
    customerId: 'cust-001',
    insightType: 'tax_saving',
    title: 'Maximise Section 80C deductions',
    description:
      'This customer has utilised only ₹70,000 of the ₹1,50,000 Section 80C limit for FY 2024-25. Additional ELSS or PPF contributions before 31 March could reduce taxable income.',
    recommendations: [
      'Invest ₹80,000 more in ELSS or PPF before the financial year ends',
      'Confirm existing LIC premium is being claimed under 80C',
    ],
    confidenceScore: 0.92,
    status: 'active',
    createdAt: daysAgo(2),
    updatedAt: daysAgo(2),
  },
  {
    id: 'ins-002',
    customerId: 'cust-002',
    insightType: 'deduction',
    title: 'Unclaimed home loan interest under Section 24(b)',
    description:
      'A self-occupied property loan appears in the customer profile but no interest deduction under Section 24(b) has been recorded. Up to ₹2,00,000 may be deductible.',
    recommendations: [
      'Collect the lender interest certificate for FY 2024-25',
      'Claim eligible interest under Section 24(b) in the ITR',
    ],
    confidenceScore: 0.84,
    status: 'active',
    createdAt: daysAgo(4),
    updatedAt: daysAgo(4),
  },
  {
    id: 'ins-003',
    customerId: 'cust-003',
    insightType: 'deadline',
    title: 'Advance tax instalment due soon',
    description:
      'Estimated tax liability exceeds ₹10,000 for the year. The next advance tax instalment is due on 15 June; missing it attracts interest under Section 234C.',
    recommendations: [
      'Compute the Q1 advance tax instalment (15% of estimated liability)',
      'Schedule the challan payment before 15 June',
    ],
    confidenceScore: 0.97,
    status: 'active',
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1),
  },
  {
    id: 'ins-004',
    customerId: 'cust-001',
    insightType: 'risk_alert',
    title: 'Form 26AS / AIS mismatch detected',
    description:
      'Interest income reported in the AIS is higher than the amount in the draft return. An unreconciled mismatch increases the likelihood of a notice under Section 143(1).',
    recommendations: [
      'Reconcile bank/FD interest against the AIS before filing',
      'Add the missing interest income to the return',
    ],
    confidenceScore: 0.78,
    status: 'pending_review',
    createdAt: daysAgo(6),
    updatedAt: daysAgo(3),
  },
  {
    id: 'ins-005',
    customerId: 'cust-004',
    insightType: 'investment',
    title: 'NPS contribution for additional ₹50,000 deduction',
    description:
      'The customer is in the 30% slab and has no NPS contribution. Section 80CCD(1B) allows an extra ₹50,000 deduction over and above the 80C limit.',
    recommendations: [
      'Open or top up a Tier-I NPS account',
      'Contribute up to ₹50,000 to claim under 80CCD(1B)',
    ],
    confidenceScore: 0.88,
    status: 'active',
    createdAt: daysAgo(8),
    updatedAt: daysAgo(8),
  },
  {
    id: 'ins-006',
    customerId: 'cust-002',
    insightType: 'optimization',
    title: 'Old vs new regime comparison favours old regime',
    description:
      'Based on declared deductions, the old tax regime saves approximately ₹41,000 versus the new regime for this customer this year.',
    recommendations: [
      'Elect the old regime when filing this ITR',
      'Re-evaluate the choice each year as deductions change',
    ],
    confidenceScore: 0.9,
    status: 'applied',
    createdAt: daysAgo(15),
    updatedAt: daysAgo(10),
  },
  {
    id: 'ins-007',
    customerId: 'cust-005',
    insightType: 'compliance',
    title: 'GST return GSTR-3B not yet filed',
    description:
      'The customer is GST-registered and the GSTR-3B for the latest period is still pending. Late filing attracts a per-day late fee and interest.',
    recommendations: [
      'File the pending GSTR-3B before the due date',
      'Set up a recurring reminder for monthly GST filings',
    ],
    confidenceScore: 0.95,
    status: 'active',
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3),
  },
  {
    id: 'ins-008',
    customerId: 'cust-003',
    insightType: 'regulatory',
    title: 'Updated TDS rate on rent under Section 194-IB',
    description:
      'A regulatory update affects TDS on rent paid above ₹50,000/month. Verify the deduction rate applied to this customer’s rental payments.',
    recommendations: [
      'Review rent payments above ₹50,000/month',
      'Apply the revised TDS rate and deposit any shortfall',
    ],
    confidenceScore: 0.71,
    status: 'dismissed',
    createdAt: daysAgo(20),
    updatedAt: daysAgo(12),
  },
]
